import React from 'react';

import { useNavigate } from 'react-router';

import { useAtomValue } from 'jotai';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { useTranslation } from 'react-i18next';

import { ButtonVariant } from '@cfa/react-components';
import { Colors } from '@cfa/react-components/dist/cjs/internal/types';

import { getUpdatedToggleVisibleDocuments } from '@/utils/adminEdit';

import {
  IntlCategoryDTO,
  IntlSubcategoryDTO,
  IntlSubtitleDTO,
} from '@/services/content-api-types';

import ConfirmationModal from 'components/popups/ConfirmationModal';
import { getNameFromLanguage, itemByLanguage } from 'utils/language';

import { userPrefsAtom } from '@/atoms/userPrefs';
import { deleteCategory } from '@/routes/Admin/components/EditCategory/actions';
import { useEditCategory } from '@/routes/Admin/hooks';
import { setAdminState, useFocusAdminState } from '@/routes/Admin/state';

interface ConfirmationModalsProps {
  category: IntlCategoryDTO;
  countryCode: string;
  onShowToast: (name: string) => void;
}

const ConfirmationModals: React.FC<ConfirmationModalsProps> = ({
  category,
  countryCode,
  onShowToast,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { temporaryCategory, setTemporaryCategory } =
    useFocusAdminState('temporaryCategory');
  const { confirmationModalType } = useFocusAdminState('confirmationModalType');
  const { selectedCountry } = useFocusAdminState('selectedCountry');
  const { selectedDocument, setSelectedDocument } =
    useFocusAdminState('selectedDocument');
  const { selectedSubcategoryIndex, setSelectedSubcategoryIndex } =
    useFocusAdminState('selectedSubcategoryIndex');
  const { selectedSubtitleIndex, setSelectedSubtitleIndex } =
    useFocusAdminState('selectedSubtitleIndex');
  const { selectedSubtitle, setSelectedSubtitle } =
    useFocusAdminState('selectedSubtitle');
  const { documentIsVisibleInSubtitle } = useFocusAdminState(
    'documentIsVisibleInSubtitle',
  );
  const { selectedSubcategory } = useFocusAdminState('selectedSubcategory');
  const userPrefs = useAtomValue(userPrefsAtom);

  const { mutateAsync: editCategory } = useEditCategory();

  const resetSubtitleMutationState = () => {
    setSelectedSubcategoryIndex(-1);
    setSelectedSubtitleIndex(-1);
    setSelectedDocument(null);
    setAdminState('confirmationModalType', null);
  };

  // Apparently this doesn't work all the time? I am adding another cleanup function in EditCategory.tsx
  // https://cfacorp.atlassian.net/browse/PATHWAY-2389
  const deleteDocumentFromSubtitle = async () => {
    const documentId = selectedDocument?.id;
    const documentName = selectedDocument?.name;
    setSelectedSubtitle({
      ...selectedSubtitle,
      visibleDocuments: documentIsVisibleInSubtitle
        ? selectedSubtitle?.visibleDocuments?.filter(id => id !== documentId)
        : selectedSubtitle?.visibleDocuments,
      documents: selectedSubtitle?.documents.filter(
        ({ id }) => id !== documentId,
      ),
    } as IntlSubtitleDTO);

    const mapSubcategoriesFunc = (
      subcat: IntlSubcategoryDTO,
      mappedSubcatIndex: number,
    ) =>
      mappedSubcatIndex === selectedSubcategoryIndex
        ? {
            ...subcat,
            subtitles: [
              ...(subcat.subtitles?.map(
                (mappedSubtitle, mappedSubtitleIndex) =>
                  mappedSubtitleIndex === selectedSubtitleIndex
                    ? {
                        ...mappedSubtitle,
                        documents: mappedSubtitle.documents.filter(
                          doc => doc.id !== selectedDocument?.id,
                        ),
                        visibleDocuments:
                          mappedSubtitle.visibleDocuments?.filter(
                            docId => docId !== selectedDocument?.id,
                          ),
                      }
                    : mappedSubtitle,
              ) ?? []),
            ],
          }
        : subcat;

    const updatedTemporaryCategory = {
      ...category,
      subcategories: temporaryCategory.subcategories
        ? temporaryCategory.subcategories.map(mapSubcategoriesFunc)
        : [],
    };

    // TODO: `index` does not exist on DocumentDTO. This cast suppresses the
    // type error but the lookup theoretically resolves to `documents?.[undefined]` every time,
    // making this condition always false and skipping the editCategory call.
    // This needs looking into further.
    if (
      selectedDocument?.id ===
      category.subcategories?.[selectedSubcategoryIndex]?.subtitles?.[
        selectedSubtitleIndex
      ]?.documents[
        (selectedDocument as { index: number } | undefined)?.index ?? 0
      ]?.id
    ) {
      const updatedCategory = {
        ...category,

        subcategories: (category.subcategories ?? []).map(mapSubcategoriesFunc),
      };

      await editCategory({
        payload: updatedCategory,
        categoryName: itemByLanguage(documentName),
      });
    }
    setAdminState('selectedDocument', null);
    setAdminState('confirmationModalType', null);
    setTemporaryCategory(updatedTemporaryCategory);
  };

  const onToggleSubtitleDocumentVisibility = () => {
    const documentId = selectedDocument?.id;
    if (!documentId) return;
    const mutableCategory = cloneDeep(temporaryCategory);

    const mutableCategorySubtitle =
      // @ts-expect-error subtitles array index access not typed correctly
      mutableCategory.subcategories[selectedSubcategoryIndex].subtitles[
        selectedSubtitleIndex
      ];

    const documentIsCurrentlyVisible =
      // @ts-expect-error visibleDocuments not typed on mutableCategorySubtitle
      mutableCategorySubtitle.visibleDocuments.includes(documentId);

    setSelectedSubtitle({
      ...selectedSubtitle,
      visibleDocuments: getUpdatedToggleVisibleDocuments(
        documentId,
        documentIsCurrentlyVisible,
        selectedSubtitle?.visibleDocuments,
        selectedSubtitle?.documents,
      ),
    } as IntlSubtitleDTO);

    const updatedVisibleDocuments = getUpdatedToggleVisibleDocuments(
      documentId,
      documentIsCurrentlyVisible,
      mutableCategorySubtitle?.visibleDocuments,
      mutableCategorySubtitle?.documents,
    );

    // @ts-expect-error subtitles array index access not typed correctly
    mutableCategory.subcategories[selectedSubcategoryIndex].subtitles[
      selectedSubtitleIndex
    ].visibleDocuments = updatedVisibleDocuments;

    resetSubtitleMutationState();
    setTemporaryCategory(mutableCategory);
    setSelectedDocument(null);
  };

  const onToggleSubcategoryVisibility = () => {
    if (!temporaryCategory.subcategories || !selectedSubcategory) return;
    setTemporaryCategory({
      ...temporaryCategory,
      subcategories: temporaryCategory.subcategories.map(subcat => {
        if (subcat.id === selectedSubcategory.id) {
          return { ...subcat, enabled: !subcat.enabled };
        }
        return subcat;
      }),
    });
    setAdminState('confirmationModalType', null);
  };

  const onDeleteSubcategory = async () => {
    const futureSubcategories = [...(temporaryCategory.subcategories ?? [])];
    const subcatIndex = futureSubcategories.findIndex(
      subcat => subcat.id === selectedSubcategory?.id,
    );
    if (subcatIndex === -1) return;

    const removedSubcategory = futureSubcategories.splice(subcatIndex, 1);

    setTemporaryCategory({
      ...temporaryCategory,
      subcategories: futureSubcategories,
    });
    await editCategory({
      payload: {
        ...category,
        subcategories: futureSubcategories,
      },
      categoryName: itemByLanguage(removedSubcategory.at(0)?.name),
    });

    setAdminState('selectedSubcategory', null);
    setAdminState('selectedSubcategoryIndex', -1);
    setAdminState('confirmationModalType', null);
  };

  const onDeleteSection = async () => {
    if (selectedSubtitle && selectedSubcategory) {
      const futureSubcategories = [...(temporaryCategory.subcategories ?? [])];

      const futureCategoryIndex = (
        temporaryCategory.subcategories ?? []
      ).findIndex(subcat => subcat.id === selectedSubcategory.id);

      const futureSubtitles = [
        ...([...(temporaryCategory.subcategories ?? [])][futureCategoryIndex]
          ?.subtitles ?? []),
      ];
      // checking to make sure we are not in editing mode
      if (isEqual(temporaryCategory, category)) {
        await editCategory({
          payload: {
            ...category,
            subcategories: futureSubcategories.map(it =>
              it.id === selectedSubcategory.id
                ? {
                    ...it,
                    subtitles: it.subtitles?.filter(
                      subtitle => subtitle.id !== selectedSubtitle.id,
                    ),
                  }
                : it,
            ),
          },
          categoryName: itemByLanguage(category.name),
        });
      }
      const removedSubtitle = futureSubtitles.splice(selectedSubtitleIndex, 1);

      setTemporaryCategory({
        ...temporaryCategory,
        subcategories: futureSubcategories.map((it, ind) =>
          ind === selectedSubcategoryIndex
            ? {
                ...it,
                subtitles: it.subtitles?.filter(
                  (_, idx) => idx !== selectedSubtitleIndex,
                ),
              }
            : it,
        ),
      });

      // We guard against adding a subtitle and deleting it before it's saved
      const name = itemByLanguage(removedSubtitle.at(0)?.name);
      if (name) {
        onShowToast(name);
      }
    }
    setAdminState('confirmationModalType', null);
  };
  const handleCategoryVisibility = () => {
    setTemporaryCategory({
      ...temporaryCategory,
      enabled: !temporaryCategory.enabled,
    });
    setAdminState('confirmationModalType', null);
  };

  const handleCategoryDelete = () => {
    deleteCategory(itemByLanguage(temporaryCategory.name));
    navigate('/admin');
  };

  const getModalProps = () => {
    switch (confirmationModalType) {
      case 'categoryVisibility':
        return {
          bodyText: temporaryCategory.enabled
            ? t('Admin.makeHiddenCategoryBody', {
                categoryName: temporaryCategory.name
                  ? getNameFromLanguage(temporaryCategory.name)
                  : undefined,
                countryCode: selectedCountry.label,
              })
            : t('Admin.makeVisibleCategoryBody', {
                categoryName: temporaryCategory.name
                  ? getNameFromLanguage(temporaryCategory.name)
                  : undefined,
                countryCode: selectedCountry.label,
              }),
          headerText: temporaryCategory.enabled
            ? t('Admin.makeHiddenHeader')
            : t('Admin.makeVisibleHeader'),
          primaryButtonColor: temporaryCategory.enabled
            ? 'primary'
            : 'secondary',
          primaryButtonText: temporaryCategory.enabled
            ? t('Button.makeHidden')
            : t('Button.makeVisible'),
          primaryButtonHandler: handleCategoryVisibility,
        };
      case 'categoryDelete':
        return {
          bodyText: t('Generic.deleteObject', {
            name: temporaryCategory.name
              ? getNameFromLanguage(temporaryCategory.name)
              : '',
            object: temporaryCategory.featured
              ? t('Admin.recommendedCategory').toLowerCase()
              : t('Admin.category').toLowerCase(),
          }),
          headerText: t('Generic.deleteHeader', {
            type: temporaryCategory.featured
              ? t('Admin.recommendedCategory')
              : t('Admin.category'),
          }),
          primaryButtonColor: temporaryCategory.enabled
            ? 'primary'
            : 'secondary',
          primaryButtonText: t('Button.delete'),
          primaryButtonHandler: handleCategoryDelete,
          primaryButtonVariant: 'destructive',
        };
      case 'subcategoryVisibility':
        return {
          bodyText: selectedSubcategory?.enabled
            ? t('Admin.makeHiddenSubcategoryBody', {
                categoryName: itemByLanguage(selectedSubcategory.name, ''),
                countryName: countryCode,
              })
            : t('Admin.makeVisibleSubcategoryBody', {
                categoryName: itemByLanguage(selectedSubcategory?.name, ''),
                countryName: countryCode,
              }),
          headerText: selectedSubcategory?.enabled
            ? t('Admin.makeHiddenHeader')
            : t('Admin.makeVisibleHeader'),
          primaryButtonColor: selectedSubcategory?.enabled
            ? 'primary'
            : 'secondary',
          primaryButtonText: selectedSubcategory?.enabled
            ? t('Button.makeHidden')
            : t('Button.makeVisible'),
          primaryButtonHandler: onToggleSubcategoryVisibility,
        };
      case 'subcategoryDelete':
        return {
          bodyText: t('Generic.deleteObject', {
            name: selectedSubcategory?.name
              ? itemByLanguage(selectedSubcategory.name, '')
              : '',
            object: t('Admin.subcategory').toLowerCase(),
          }),
          headerText: t('Generic.deleteHeader', {
            type: t('Admin.subcategory'),
          }),
          primaryButtonText: t('Button.delete'),
          primaryButtonHandler: onDeleteSubcategory,
          primaryButtonColor: 'primary',
        };
      case 'sectionDelete':
        return {
          bodyText: t('Generic.deleteObject', {
            name: selectedSubtitle
              ? itemByLanguage(selectedSubtitle.name, '')
              : '',
            object: t('Generic.section').toLowerCase(),
          }),
          headerText: t('Generic.deleteHeader', {
            type: t('Generic.section'),
          }),
          primaryButtonText: t('Button.delete'),
          primaryButtonHandler: onDeleteSection,
          primaryButtonColor: 'primary',
        };
      case 'documentVisibility':
        return {
          bodyText: documentIsVisibleInSubtitle
            ? t('Admin.makeHiddenDocumentBody', {
                countryName: userPrefs.country.id ?? '',
                documentName: selectedDocument
                  ? itemByLanguage(selectedDocument.name)
                  : '',
              })
            : t('Admin.makeVisibleDocumentBody', {
                countryName: userPrefs.country.id ?? '',
                documentName: selectedDocument
                  ? itemByLanguage(selectedDocument.name)
                  : '',
              }),
          headerText: documentIsVisibleInSubtitle
            ? t('Admin.makeHiddenHeader')
            : t('Admin.makeVisibleHeader'),
          primaryButtonColor: documentIsVisibleInSubtitle
            ? 'primary'
            : 'secondary',
          primaryButtonText: documentIsVisibleInSubtitle
            ? t('Button.makeHidden')
            : t('Button.makeVisible'),
          primaryButtonHandler: onToggleSubtitleDocumentVisibility,
          primaryButtonVariant: 'destructive',
          secondaryButtonHandler: resetSubtitleMutationState,
          onClose: resetSubtitleMutationState,
        };
      case 'documentDelete':
        return {
          bodyText: t('Generic.removeObject', {
            name: selectedDocument ? itemByLanguage(selectedDocument.name) : '',
            object: t('Generic.resource').toLowerCase(),
          }),
          headerText: t('Generic.removeHeader', {
            type: t('Generic.resource'),
          }),
          primaryButtonText: t('Button.delete'),
          primaryButtonHandler: deleteDocumentFromSubtitle,
          primaryButtonColor: 'primary',
          secondaryButtonHandler: resetSubtitleMutationState,
          onClose: resetSubtitleMutationState,
        };
    }
  };

  return confirmationModalType ? (
    <>
      <ConfirmationModal
        bodyText={getModalProps()?.bodyText ?? ''}
        headerText={getModalProps()?.headerText ?? ''}
        isOpen={!!confirmationModalType}
        onClose={e => {
          e.stopPropagation();
          getModalProps()?.onClose?.();
          setAdminState('confirmationModalType', null);
        }}
        primaryButtonHandler={e => {
          e.stopPropagation();
          getModalProps()?.primaryButtonHandler();
        }}
        primaryButtonColor={getModalProps()?.primaryButtonColor as Colors}
        primaryButtonText={getModalProps()?.primaryButtonText ?? ''}
        primaryButtonVariant={
          getModalProps()?.primaryButtonVariant as ButtonVariant | undefined
        }
        secondaryButtonHandler={e => {
          e.stopPropagation();
          getModalProps()?.secondaryButtonHandler?.();
          setAdminState('confirmationModalType', null);
        }}
        secondaryButtonText={t('Button.cancel')}
      />
    </>
  ) : null;
};

export default ConfirmationModals;
